import { iamLog } from '@identity/application/observability';
import type { IAuthApiPort, ILinkTokenStoragePort } from '@identity/application/ports';
import { subjectAttributes } from '@identity/application/utils';
import { TOKEN_PERSISTING_LINK_PURPOSES } from '@identity/domain/types';
import type { LinkPurpose } from '@identity/domain/types';

/** Whether the flow of this purpose continues in this browser and needs a stored session. */
const needsStoredToken = (purpose: LinkPurpose): boolean =>
  (TOKEN_PERSISTING_LINK_PURPOSES as readonly LinkPurpose[]).includes(purpose);

/**
 * Consumes the magic link and, only for the purposes that need it, opens the link session the rest
 * of the flow runs on.
 *
 * Two different tokens meet here. The one it receives is public, travelled in an email and
 * authorizes exactly this call; it is never stored. The response brings back a different one, and
 * that is what the wizard uses from here on, together with the account id, which no other response
 * of this domain reports.
 *
 * The session is written only when the call succeeded and the purpose continues in this browser: a
 * link whose only screen makes no request would just overwrite the session of a wizard open in
 * another tab.
 *
 * A failure logs nothing on purpose. This runs before the link session exists and the error body
 * does not name the account, so a log here would report that some consume failed somewhere, which
 * reads like data and is not.
 *
 * It takes no flow, and its event carries none: this runs before the flow is known, because the
 * landing serves every purpose and only the answer says which one arrived. The purpose it does log
 * is strictly more precise, since it names the link instead of the journey containing it.
 */
export const consumeMagicLinkUseCase = (authApi: IAuthApiPort, linkTokenStorage: ILinkTokenStoragePort) => {
  /** The caller only gets the purpose, to choose the route: the token and the account stay here. */
  const execute = async (magicLinkToken: string): Promise<{ purpose: LinkPurpose }> => {
    const { purpose, userId, linkToken } = await authApi.consumeMagicLink(magicLinkToken);

    if (needsStoredToken(purpose)) {
      linkTokenStorage.save(linkToken);
      linkTokenStorage.saveUserId(userId);
    }

    iamLog.info('magicLinkConsumed', { ...subjectAttributes(userId), 'iam.purpose': purpose });

    return { purpose };
  };

  return { execute };
};
